/* utils/services/StadiaGeocodingService.ts --------------------------------- *
 * Service for Stadia Maps Geocoding API - search, autocomplete, reverse       *
 * --------------------------------------------------------------------------- */

const STADIA_BASE_URL = 'https://api.stadiamaps.com/geocoding/v1';

function getApiKey(): string {
  const meta = document.querySelector('meta[name="stadia-api-key"]');
  return meta?.getAttribute('content') || '';
}

export interface StadiaGeocodingResult {
  place_id: string;
  licence: string;
  osm_type: string;
  osm_id: string;
  boundingbox: [string, string, string, string];
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
    neighbourhood?: string;
    name?: string;
  };
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

interface StadiaFeature {
  type: string;
  geometry: {
    type: string;
    coordinates: [number, number];
  };
  bbox?: [number, number, number, number];
  properties: {
    gid?: string;
    id?: string;
    layer?: string;
    source?: string;
    source_id?: string;
    name?: string;
    label?: string;
    confidence?: number;
    country?: string;
    country_a?: string;
    region?: string;
    region_a?: string;
    county?: string;
    locality?: string;
    neighbourhood?: string;
    postalcode?: string;
    street?: string;
    housenumber?: string;
    [key: string]: unknown;
  };
}

interface StadiaResponse {
  type: string;
  features: StadiaFeature[];
  bbox?: [number, number, number, number];
}

function mapFeatureToResult(feature: StadiaFeature): StadiaGeocodingResult {
  const props = feature.properties;
  const [lon, lat] = feature.geometry.coordinates;

  // Stadia bbox is [west, south, east, north] → LocationIQ boundingbox is [south, north, west, east]
  let boundingbox: [string, string, string, string];
  if (feature.bbox) {
    boundingbox = [
      String(feature.bbox[1]),
      String(feature.bbox[3]),
      String(feature.bbox[0]),
      String(feature.bbox[2])
    ];
  } else {
    boundingbox = [String(lat), String(lat), String(lon), String(lon)];
  }

  return {
    place_id: props.gid || props.id || '',
    licence: 'https://stadiamaps.com/attribution',
    osm_type: props.source === 'openstreetmap' ? (props.source_id?.charAt(0) === 'w' ? 'way' : 'node') : '',
    osm_id: props.source_id || '',
    boundingbox,
    lat: String(lat),
    lon: String(lon),
    display_name: props.label || props.name || '',
    class: props.layer || '',
    type: props.layer || '',
    importance: props.confidence || 0,
    address: {
      house_number: props.housenumber,
      road: props.street,
      suburb: props.neighbourhood,
      city: props.locality,
      county: props.county,
      state: props.region,
      postcode: props.postalcode,
      country: props.country,
      country_code: props.country_a?.toLowerCase(),
      neighbourhood: props.neighbourhood,
      name: props.name
    }
  };
}

class StadiaGeocodingService {
  private static _instance: StadiaGeocodingService;

  static get shared(): StadiaGeocodingService {
    if (!StadiaGeocodingService._instance) {
      StadiaGeocodingService._instance = new StadiaGeocodingService();
    }
    return StadiaGeocodingService._instance;
  }

  private constructor() {
    if (StadiaGeocodingService._instance) {
      throw new Error('Use StadiaGeocodingService.shared, not constructor.');
    }
  }

  /**
   * Search for addresses using Stadia forward geocoding
   */
  async searchAddress(query: string): Promise<StadiaGeocodingResult[]> {
    try {
      const encodedQuery = encodeURIComponent(query);
      const url = `${STADIA_BASE_URL}/search?text=${encodedQuery}&api_key=${getApiKey()}&size=5`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Stadia Geocoding API error: ${response.status} ${response.statusText}`);
      }

      const data: StadiaResponse = await response.json();
      return data.features.map(mapFeatureToResult);
    } catch (error) {
      console.error('Stadia search failed:', error);
      throw error;
    }
  }

  /**
   * Autocomplete search using Stadia
   */
  async autocomplete(query: string): Promise<StadiaGeocodingResult[]> {
    try {
      const encodedQuery = encodeURIComponent(query);
      const url = `${STADIA_BASE_URL}/autocomplete?text=${encodedQuery}&api_key=${getApiKey()}&size=5`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Stadia Autocomplete API error: ${response.status} ${response.statusText}`);
      }

      const data: StadiaResponse = await response.json();
      return data.features.map(mapFeatureToResult);
    } catch (error) {
      console.error('Stadia autocomplete failed:', error);
      throw error;
    }
  }

  /**
   * Get coordinates from a geocoding result
   */
  getCoordinatesFromResult(result: StadiaGeocodingResult): Coordinates {
    return {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon)
    };
  }

  /**
   * Format display name from result
   */
  formatDisplayName(result: StadiaGeocodingResult): string {
    return result.display_name;
  }
}

export const stadiaGeocodingService = StadiaGeocodingService.shared;
